import { useInfiniteQuery } from '@tanstack/react-query';
import { get } from '../api/api';
import { API_URLS } from '../constants/apiUrl';
import { QueryParamObject, toQueryString } from '../constants/utilityFunctions';

interface IBusinessAreasParameters {
  sortBy?: string[];
  extraFilters?: Record<string, string | number>;
  pageSize?: number;
  search?: string;
}

export const useFetchBusinessAreas = (params: IBusinessAreasParameters, enabled: boolean) => {
  const fetchBusinessAreas = async ({ pageParam = 1 }) => {
    const { extraFilters: filters, ...rest } = params;
    const queryString = new URLSearchParams(
      Object.keys(rest)?.length ? toQueryString(rest as QueryParamObject) : '',
    );

    Object.entries(filters ?? {}).forEach(([key, value]) => {
      queryString.append(`filter.${key}`, String(value));
    });

    queryString.append('page', String(pageParam));
    queryString.append('limit', String(params.pageSize));
    try {
      return await get(`${API_URLS.BUSINESS_AREAS_LIST}?${queryString.toString()}`);
    } catch (error) {
      console.error('Error fetching business areas:', error);
      throw new Error((error as { message: string }).message || 'Failed to fetch business areas');
    }
  };

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['business-areas', params],
    queryFn: fetchBusinessAreas,
    enabled: enabled,
    getNextPageParam: (lastPage, allPages) => {
      const totalPageCount = lastPage?.meta?.totalItems ?? 0;
      const fetchedPageCount = allPages.flatMap((page) => page.data).length;
      return fetchedPageCount < totalPageCount ? allPages.length + 1 : undefined;
    },
  });
  return {
    data: data?.pages.flatMap((page) => page.data) ?? [],
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  };
};
